import { zodResolver } from '@hookform/resolvers/zod'
import { Password } from '@numistic/contracts'
import { useResetPassword } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Field, Input } from '@numistic/ui'
import { createRoute, Link } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AuthCard } from '../../components/auth-card.js'
import { useErrorText } from '../../lib/errors.js'
import { rootRoute } from '../root.js'

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  // Better Auth перенаправляет сюда со ссылки из письма: ?token=… либо ?error=INVALID_TOKEN.
  validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }),
  component: ResetPasswordPage,
})

const FormSchema = z.object({ newPassword: Password })

function ResetPasswordPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { token, error } = resetPasswordRoute.useSearch()
  const reset = useResetPassword()
  const form = useForm({ resolver: zodResolver(FormSchema), defaultValues: { newPassword: '' } })

  const onSubmit = form.handleSubmit(({ newPassword }) =>
    reset.mutate({ token: token!, newPassword }),
  )
  const backLink = (
    <Link to="/login" className="text-center text-sm text-muted-foreground hover:text-foreground">
      {t('auth.backToSignIn')}
    </Link>
  )

  if (error || !token) {
    return (
      <AuthCard title={t('auth.resetTitle')}>
        <div className="grid gap-4">
          <Alert variant="destructive">
            {error ? t('errors.INVALID_TOKEN') : t('auth.resetNoToken')}
          </Alert>
          <Link
            to="/forgot-password"
            className="text-center text-sm text-muted-foreground hover:text-foreground"
          >
            {t('auth.forgotTitle')}
          </Link>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard title={t('auth.resetTitle')}>
      {reset.isSuccess ? (
        <div className="grid gap-4">
          <Alert variant="success">{t('auth.resetDone')}</Alert>
          {backLink}
        </div>
      ) : (
        <form className="grid gap-4" onSubmit={onSubmit} noValidate>
          {reset.error ? (
            <Alert variant="destructive">{errorText.request(reset.error)}</Alert>
          ) : null}
          <Field
            label={t('auth.newPassword')}
            hint={t('auth.passwordHint')}
            error={errorText.field(form.formState.errors.newPassword)}
          >
            <Input
              type="password"
              autoComplete="new-password"
              autoFocus
              {...form.register('newPassword')}
            />
          </Field>
          <Button type="submit" disabled={reset.isPending}>
            {t('auth.resetSubmit')}
          </Button>
        </form>
      )}
    </AuthCard>
  )
}
